import CustomSwitch from "../../components/CustomSwitch";
import { useEffect, useState } from "react";
import { useSnapshot } from "valtio";
import { getWedding } from "../../apis/wedding/get.wedding";
import { updateWebsiteVisibility } from "../../apis/wedding/update.website.visibility";
import { editorState } from "../../utils/editor";

export default function WebsiteSettings() {
  const { XAccountHash, weddingID } = useSnapshot(editorState);
  const [disabled, setDisabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoaded(false);
    setError("");
    getWedding(
      {
        headers: { "X-Account-Hash": XAccountHash || "" },
        onCompleted: ({ data }) => {
          if (active) {
            setDisabled(Boolean(data.websiteDisabled));
            setLoaded(true);
            setLoading(false);
          }
        },
        onError: () => {
          if (active) {
            setError("Could not load website settings. Refresh to try again.");
            setLoading(false);
          }
        },
      },
      { bypassCache: true }
    );
    return () => {
      active = false;
    };
  }, [XAccountHash, weddingID]);

  const save = (enabled: boolean) => {
    setSaving(true);
    setError("");
    setNotice("");
    updateWebsiteVisibility({
      headers: { "X-Account-Hash": XAccountHash || "" },
      body: { websiteDisabled: !enabled },
      onCompleted: ({ data }) => {
        setDisabled(Boolean(data.websiteDisabled));
        setSaving(false);
        setNotice(
          data.websiteDisabled
            ? "Your website is now offline."
            : "Your website is now visible to visitors."
        );
      },
      onError: () => {
        setSaving(false);
        setError("Could not update website visibility. Please try again.");
      },
    });
  };

  return (
    <section
      className="w-full p-4 font-['DM_Sans',sans-serif] text-[#161815] md:p-8 lg:p-10"
      aria-busy={loading || saving}
    >
      <div className="max-w-[620px]">
        <h2 className="text-balance text-[22px] font-normal md:text-[26px]">Website visibility</h2>
        <p className="mt-1 text-pretty text-sm text-[#777C74]">
          Choose whether guests can visit your wedding website.
        </p>

        <div className="mt-7 flex flex-col items-start justify-between gap-4 border-y border-[#E6E8E2] py-6 sm:flex-row sm:items-center">
          <div>
            <h3 id="website-visibility-label" className="text-base font-medium">Public website</h3>
            <p className="mt-1 text-sm leading-relaxed text-[#777C74]">
              {loading
                ? "Loading settings…"
                : !loaded
                ? "Website visibility is unavailable."
                : disabled
                ? "Your website is offline and hidden from guests."
                : "Your website is online and visible to guests."}
            </p>
          </div>
          <div role="group" aria-labelledby="website-visibility-label" className="shrink-0">
            <CustomSwitch
              options={[
                { text: "Online", value: "online" },
                { text: "Offline", value: "offline" },
              ]}
              value={loaded ? (disabled ? "offline" : "online") : ""}
              isDisabled={!loaded || loading || saving || !weddingID}
              onChange={(value) => save(value === "online")}
            />
          </div>
        </div>

        <p className="mt-5 text-sm leading-relaxed text-[#777C74]">
          Taking your website offline keeps your content saved. You can bring it back online at any time.
        </p>
        <p className="mt-2 text-sm leading-relaxed text-[#777C74]">
          Your standalone Aso Ebi and Love Wall links will still work.
        </p>
        {error && <p role="alert" className="mt-4 text-sm text-red-700">{error}</p>}
        <p role="status" className="text-sm text-[#2E563B] [&:not(:empty)]:mt-4">
          {saving ? "Saving…" : notice}
        </p>
      </div>
    </section>
  );
}
